import { Subject } from 'rxjs';

import { Rectangle } from '../../../../types/geo.types';
import { EventAbsolutePosition, GeoUtils } from '../../../../utils/geo.utils';
import { HTMLUtils } from '../../../../utils/html.utils';
import { toNumber } from '../../../../utils/lodash-min';
import { ImageContainer } from './image-container';

export class ElementGeoData {
    private readonly height: number;
    private readonly width: number;

    constructor(el: HTMLElement) {
        this.height = el.getBoundingClientRect().height;
        this.width = el.getBoundingClientRect().width;
    }

    getWidth() {
        return this.width;
    }

    getHeight() {
        return this.height;
    }
}

export class AbsoluteElementGeoData extends ElementGeoData {
    private readonly top: number;
    private readonly left: number;

    constructor(el: HTMLElement, readonly parent: HTMLElement) {
        super(el);
        this.top = el.getBoundingClientRect().top - parent.getBoundingClientRect().top;
        this.left = el.getBoundingClientRect().left - parent.getBoundingClientRect().left;
    }

    getTop() {
        return this.top;
    }

    getLeft() {
        return this.left;
    }

    getParentWidth() {
        return this.parent.getBoundingClientRect().width;
    }

    getParentHeight() {
        return this.parent.getBoundingClientRect().height;
    }
}

export class ResizeMoveHandlerData {
    private readonly north: boolean;
    private readonly east: boolean;
    private readonly west: boolean;
    private readonly south: boolean;
    private readonly hasResizeHandlerClass: boolean;
    private readonly hasMoveHandlerClass: boolean;

    hasNorth(): boolean {
        return this.north;
    }
    hasEast(): boolean {
        return this.east;
    }
    hasWest(): boolean {
        return this.west;
    }
    hasSouth(): boolean {
        return this.south;
    }

    isResizeEvent() {
        return this.hasResizeHandlerClass;
    }

    isMoveEvent() {
        return this.hasMoveHandlerClass;
    }

    constructor(el: HTMLElement) {
        this.north = el.classList.contains('n');
        this.east = el.classList.contains('e');
        this.west = el.classList.contains('w');
        this.south = el.classList.contains('s');
        this.hasMoveHandlerClass = el.classList.contains('move_handle');
        this.hasResizeHandlerClass = el.classList.contains('resize_handle');
    }
}

export class MouseDownEventSnapshot {
    private readonly _focusElementGeoData: AbsoluteElementGeoData;
    private readonly eventAbsolutePosition;
    constructor(event: MouseEvent, focusElementGeoData: AbsoluteElementGeoData) {
        this._focusElementGeoData = focusElementGeoData;
        this.eventAbsolutePosition = new EventAbsolutePosition(event);
    }

    getPageX() {
        return this.eventAbsolutePosition._pageX;
    }

    getPageY() {
        return this.eventAbsolutePosition._pageY;
    }

    getEventAbsolutePostion() {
        return this.eventAbsolutePosition;
    }

    getFocusElementGeoData() {
        return this._focusElementGeoData;
    }
}

export interface ResizeOptions {
    boundedByParent: boolean;
    keepRatio: boolean;
    ratioX: number;
    ratioY: number;
}

export class Resizer {
    constructor(
        private readonly mouseDownEventSnapshot: MouseDownEventSnapshot,
        private readonly focusElementGeoData: AbsoluteElementGeoData,
        private readonly moveResizeHandlerData: ResizeMoveHandlerData,
        private readonly resizeOptions: ResizeOptions,
    ) {
    }

    public isResizeEvent(e: MouseEvent) {
        return this.moveResizeHandlerData.isResizeEvent();
    }

    isRatioLocked() {
        return this.resizeOptions.keepRatio;
    }

    private getDirection(i: number) {
        return i < 0 ? -1 : 1;
    }

    calculateUnboundedAndUnconstrainedFocusElementNewGeoData(e: MouseEvent) {
        const delta = GeoUtils.deltaEventData(this.mouseDownEventSnapshot.getEventAbsolutePostion(), new EventAbsolutePosition(e));
        let top = this.focusElementGeoData.getTop();
        let left = this.focusElementGeoData.getLeft();
        let height = this.focusElementGeoData.getHeight();
        let width = this.focusElementGeoData.getWidth();

        if (this.moveResizeHandlerData.hasNorth()) {
            top = this.focusElementGeoData.getTop() + delta.y;
            height = this.focusElementGeoData.getHeight() - delta.y;
        }

        if (this.moveResizeHandlerData.hasWest()) {
            left = this.focusElementGeoData.getLeft() + delta.x;
            width = this.focusElementGeoData.getWidth() - delta.x;
        }

        if (this.moveResizeHandlerData.hasEast()) {
            width = this.focusElementGeoData.getWidth() + delta.x;
        }

        if (this.moveResizeHandlerData.hasSouth()) {
            height = this.focusElementGeoData.getHeight() + delta.y;
        }

        const ret = {
            top: top,
            left: left,
            height: height,
            width: width,
        };
        return ret;
    }

    // Bounded
    calculateBoundedAndUnconstrainedFocusElementNewGeoData(e: MouseEvent) {
        const unboundedUnconstrained = this.calculateUnboundedAndUnconstrainedFocusElementNewGeoData(e);
        const delta = GeoUtils.deltaEventData(this.mouseDownEventSnapshot.getEventAbsolutePostion(), new EventAbsolutePosition(e));
        let top = unboundedUnconstrained.top;
        let left = unboundedUnconstrained.left;
        let height = unboundedUnconstrained.height;
        let width = unboundedUnconstrained.width;

        if (this.moveResizeHandlerData.hasNorth()) {
            top = Math.max(this.focusElementGeoData.getTop() + delta.y, 0);
            if (top === 0) {
                height += (this.focusElementGeoData.getTop() + delta.y);
            }
        }

        if (this.moveResizeHandlerData.hasWest()) {
            left = Math.max(this.focusElementGeoData.getLeft() + delta.x, 0);
            if (left === 0) {
                width += (this.focusElementGeoData.getLeft() + delta.x);
            }
        }

        if (this.moveResizeHandlerData.hasEast()) {
            width = Math.min(this.focusElementGeoData.getWidth() + delta.x, this.focusElementGeoData.getParentWidth() - this.focusElementGeoData.getLeft());
            width = Math.max(0, width);
        }

        if (this.moveResizeHandlerData.hasSouth()) {
            height = Math.min(this.focusElementGeoData.getHeight() + delta.y, this.focusElementGeoData.getParentHeight() - this.focusElementGeoData.getTop());
            height = Math.max(0, height);
        }

        const ret = {
            top: top,
            left: left,
            height: height,
            width: width,
        };
        return ret;
    }

    calculateBoundedAndConstrainedFocusElementNewGeoData(e: MouseEvent) {
        const unboundedSquare = this.calculateUnboundedAndUnconstrainedFocusElementNewGeoData(e);
        const M = this.mouseDownEventSnapshot.getFocusElementGeoData();

        let top = unboundedSquare.top;
        let left = unboundedSquare.left;
        let height = unboundedSquare.height;
        let width = unboundedSquare.width;

        const ratio = this.resizeOptions.ratioY / this.resizeOptions.ratioX;

        if (this.moveResizeHandlerData.hasWest()) {
            if (this.moveResizeHandlerData.hasNorth()) {
                const newHeight = width * ratio;
                const newTop = M.getTop() - (newHeight - M.getHeight());
                top = newTop;
                height = newHeight;

                if (top <= 0) {
                    // top just became negative (when out of bound)
                    // need to reset left and top
                    left = left + (-1 * top * (1 / ratio));
                    width = width - (-1 * top * (1 / ratio));
                    height = height + top;
                    top = 0;
                }

                if (left <= 0) {
                    const dTopOvershoot = -1 * left;
                    top = top + dTopOvershoot * ratio;
                    height = height - dTopOvershoot * ratio;
                    width = width + left; // left is negative (left reside before 0)
                    left = 0;
                }
            }

            if (this.moveResizeHandlerData.hasSouth()) {
                const newHeight = width * ratio;
                height = newHeight;

                if (left <= 0) {
                    const deltaLeftOvershoot = -1 * left;
                    width = width + left; // left is negative (left reside before 0)
                    left = 0;

                    height = height - deltaLeftOvershoot * ratio;
                }

                if (height >= M.getParentHeight() - M.getTop()) {
                    const deltaHeight = (M.getTop() + height) - M.getParentHeight(); // expect positive
                    left = left + deltaHeight * (1 / ratio);
                    width = width - deltaHeight * (1 / ratio);

                    height = height - deltaHeight; // expect to be align with container bottom border
                }
            }
        }

        if (this.moveResizeHandlerData.hasEast()) {
            if (this.moveResizeHandlerData.hasNorth()) {
                const newHeight = width * ratio;
                const newTop = M.getTop() - (newHeight - M.getHeight());
                top = newTop;
                height = newHeight;

                if (top <= 0) {
                    // top just became negative (when out of bound)
                    // need to reset left and top
                    width = width - (-1 * top * (1 / ratio));
                    height = height + top;
                    top = 0;
                }

                const maxWidth = M.getParentWidth() - M.getLeft();
                if (width >= maxWidth) {
                    const dWidthOvershoot = -1 * (maxWidth - width); // expect dWidthOvershoot positve
                    top = top + dWidthOvershoot * ratio;
                    height = height - dWidthOvershoot * ratio;
                    width = maxWidth;
                }
            }

            if (this.moveResizeHandlerData.hasSouth()) {
                const newHeight = width * ratio;
                height = newHeight;

                const maxHeight = M.getParentHeight() - M.getTop();
                if (height >= maxHeight) {
                    const deltaHeightOvershoot = -1 * (maxHeight - height); // expect positive
                    width = width - deltaHeightOvershoot * (1 / ratio); // left is negative (left reside before 0)
                    height = maxHeight;
                }

                const maxWidth = M.getParentWidth() - M.getLeft();
                if (width >= maxWidth) {
                    const deltaWidthOvershoot = -1 * (maxWidth - width); // expect positive
                    height = height - deltaWidthOvershoot * ratio; // expect to be align with container bottom border
                    width = maxWidth;
                }
            }
        }

        const ret = {
            top: top,
            left: left,
            height: height,
            width: width,
        };

        return ret;
    }

    move(e: MouseEvent) {
        const delta = GeoUtils.deltaEventData(this.mouseDownEventSnapshot.getEventAbsolutePostion(), new EventAbsolutePosition(e));
        const M = this.focusElementGeoData;

        let top = M.getTop();
        let left = M.getLeft();
        const height = M.getHeight();
        const width = M.getWidth();

        top = Math.max(top + delta.y, 0);
        left = Math.max(left + delta.x, 0);

        if (top + height > M.getParentHeight()) {
            top = M.getParentHeight() - height;
        }

        if (left + width > M.getParentWidth()) {
            left = M.getParentWidth() - width;
        }

        const ret = {
            top: top,
            left: left,
            height: height,
            width: width,
        };

        return ret;
    }
}

export interface ResizeOptions {
    boundedByParent: boolean;
    keepRatio: boolean;
    ratioX: number;
    ratioY: number;
}

interface MouseDownInfo {
    element: HTMLElement;
    event: 'RESIZE' | 'MOVE';
    handle: HTMLElement;
    elementResizable: HTMLElement;
    top: number;
    left: number;
    width: number;
    height: number;
    target: {
        element: HTMLElement;
        top: number;
        left: number;
        width: number;
        height: number;
    };
    parent: {
        element: HTMLElement;
        top: number;
        left: number;
        width: number;
        height: number;
    };
    img;
    isAspectLocked: boolean;
}

interface MouseMoveInfo {
    top: number;
    left: number;
    abs_top: number;
    abs_left: number;
    new_top: number;
    new_left: number;
}

export class MoveResizeSquare {
    $element: HTMLElement;
    _elements;
    resizable_element: HTMLElement;
    parent: HTMLElement;
    mousedown_info: MouseDownInfo;
    mousemove_info: MouseMoveInfo;
    container: ImageContainer;

    dim: Rectangle;
    valueChanges: Subject<Rectangle>;
    isAspectLocked = true;

    resizer: Resizer;
    resizeOptions: ResizeOptions;

    constructor(container: ImageContainer, valueChanges: Subject<Rectangle>) {
        this.container = container;
        this.valueChanges = valueChanges;
        this.resizable_element = this.container.getElement().querySelector('div#elementResizable');
        this.parent = HTMLUtils.getParent(this.resizable_element);

        const containerRect = HTMLUtils.getElementBoundingRectangle(this.container.getElement());
    }

    setResizeOptions(resizeOptionFormGroupValues: any) {
        this.resizeOptions = {
            boundedByParent: true,
            keepRatio: resizeOptionFormGroupValues.keepAspectRatio,
            ratioX: this.decodeRatio(resizeOptionFormGroupValues.ratio).x,
            ratioY: this.decodeRatio(resizeOptionFormGroupValues.ratio).y,
        };
    }

    resizeMouseDown(e: MouseEvent & { target: HTMLElement, offsetY: any, offsetX: any }) {
        const focusElementGeoData = new AbsoluteElementGeoData(this.getResizableElement(), this.container.getElement());
        const mouseDownEventSnapshot = new MouseDownEventSnapshot(e, focusElementGeoData);
        const moveResizeHandlerData = new ResizeMoveHandlerData(e.target);

        this.resizer = new Resizer(mouseDownEventSnapshot, focusElementGeoData, moveResizeHandlerData, this.resizeOptions);
    }

    mouseMove(e) {
        let rect = null;
        if (this.resizer.isResizeEvent(e)) {
            if (this.resizer.isRatioLocked()) {
                rect = this.resizer.calculateBoundedAndConstrainedFocusElementNewGeoData(e);
            } else {
                rect = this.resizer.calculateBoundedAndUnconstrainedFocusElementNewGeoData(e);
            }
        } else {
            rect = this.resizer.move(e);
        }
        
        this.updateArea(rect);
    }

    public decodeRatio(ratio: { id: string }) { // TODO Preact fix, this used to be PSSelectDropdown option
        if (ratio.id === 'original') {
            return {
                x: this.container.photoModal.getEphemeralCanvas().width,
                y: this.container.photoModal.getEphemeralCanvas().height,
            };
        } else {
            const split = ratio.id.split(':');
            return {
                x: toNumber(split[0]),
                y: toNumber(split[1]),
            };
        }
    }

    tryUpdateArea(dimension: string, value: any, keepRatio: boolean, ratio: { id: string }, resetTopLeft = false) {
        this.resizeOptions.keepRatio = keepRatio;
        this.resizeOptions.ratioX = this.decodeRatio(ratio).x;
        this.resizeOptions.ratioY = this.decodeRatio(ratio).y;

        const decodedRatio = this.decodeRatio(ratio);
        const mrsGeoData = new AbsoluteElementGeoData(this.getResizableElement(), this.container.getElement());

        const top = resetTopLeft ? 0 : mrsGeoData.getTop();
        const left = resetTopLeft ? 0 : mrsGeoData.getLeft();
        let height = mrsGeoData.getHeight();
        let width = mrsGeoData.getWidth();


        if (
            (dimension === 'keepAspectRatio' && value !== false) ||
            (dimension !== 'width' && dimension !== 'height')
        ) {
            dimension = 'width';
            keepRatio = true;
            value = width;
        }

        if (dimension === 'width') {
            const targetWidth: number = value;
            const maxWidth = mrsGeoData.getParentWidth() - left;
            const deltaWidth = Math.min(targetWidth, maxWidth);
            width = deltaWidth;

            if (keepRatio) {
                height = width * decodedRatio.y / decodedRatio.x;
                const maxHeight = mrsGeoData.getParentHeight() - top;
                if (height > maxHeight) {
                    const heightOverflow = height - maxHeight; // positive
                    height = maxHeight;
                    width = width - heightOverflow * decodedRatio.x / decodedRatio.y;
                }
            }
        } else if (dimension === 'height') {
            const targetHeight: number = value;
            const maxHeight = mrsGeoData.getParentHeight() - top;
            const deltaHeight = Math.min(targetHeight, maxHeight);
            height = deltaHeight;

            if (keepRatio) {
                width = height * decodedRatio.x / decodedRatio.y;
                const maxHeight = mrsGeoData.getParentWidth() - left;
                if (width > maxHeight) {
                    const widthOverflow = width - maxHeight; // positive
                    width = maxHeight;
                    height = height - widthOverflow * decodedRatio.y / decodedRatio.x;
                }
            }
        }

        const rect = {
            top: top,
            left: left,
            height: height,
            width: width,
        };
        this.updateArea(rect);
        return rect;
    }

    public updateArea(rect: Rectangle) {
        this.updateMovableResizableElement(rect);
        this.update_faded_area();
        this.valueChanges.next(rect);
    }

    private updateMovableResizableElement(rect: Rectangle) {
        if (rect) {
            const el = document.getElementById('elementResizable');
            HTMLUtils.css(el, 'top', rect.top + 'px');
            HTMLUtils.css(el, 'left', rect.left + 'px');
            HTMLUtils.css(el, 'height', rect.height + 'px');
            HTMLUtils.css(el, 'width', rect.width + 'px');

            this.setDim(rect);
        }
    }

    public emitFakeEvent() {
        // this.valueChanges.next(Object.assign({}, this.dim, { isTrusted: false }));
    }

    public getResizableElementId() {
        return this.resizable_element.id;
    }

    public getResizableElement() {
        return this.resizable_element;
    }

    public update_faded_area() {
        const widthAndHeightOfMRS = HTMLUtils.getElementBoundingRectangle(this.getResizableElement());
        const topAndLeftOfMRS = GeoUtils.substractRects(widthAndHeightOfMRS, HTMLUtils.getParentBoundingRectangle(this.getResizableElement()));

        const parent_dim = {
            width: HTMLUtils.getParentBoundingRectangle(this.getResizableElement()).width,
            height: HTMLUtils.getParentBoundingRectangle(this.getResizableElement()).height,
        };

        const canvas_dim = HTMLUtils.getElementBoundingRectangle(this.getXCanvas());

        HTMLUtils.css(document.getElementById('top'), 'top', '0%');
        HTMLUtils.css(document.getElementById('top'), 'left', '0%');
        HTMLUtils.css(document.getElementById('top'), 'width', '100%');
        HTMLUtils.css(document.getElementById('top'), 'height', (100 * topAndLeftOfMRS.top / parent_dim.height) + '%');

        HTMLUtils.css(document.getElementById('left'), 'top', (100 * topAndLeftOfMRS.top / parent_dim.height) + '%');
        HTMLUtils.css(document.getElementById('left'), 'left', '0%');
        HTMLUtils.css(document.getElementById('left'), 'width', (100 * topAndLeftOfMRS.left / parent_dim.width) + '%');
        HTMLUtils.css(document.getElementById('left'), 'height', (100 * widthAndHeightOfMRS.height / parent_dim.height) + '%');

        HTMLUtils.css(document.getElementById('right'), 'top', (100 * topAndLeftOfMRS.top / parent_dim.height) + '%');
        HTMLUtils.css(document.getElementById('right'), 'left', (100 * ((topAndLeftOfMRS.left + widthAndHeightOfMRS.width)) / parent_dim.width) + '%');
        HTMLUtils.css(document.getElementById('right'), 'width', (100 * (parent_dim.width - (widthAndHeightOfMRS.width + topAndLeftOfMRS.left)) / parent_dim.width) + '%');
        HTMLUtils.css(document.getElementById('right'), 'height', (100 * widthAndHeightOfMRS.height / parent_dim.height) + '%');

        HTMLUtils.css(document.getElementById('bottom'), 'top', (100 * (widthAndHeightOfMRS.height + topAndLeftOfMRS.top) / parent_dim.height) + '%');
        HTMLUtils.css(document.getElementById('bottom'), 'left', '0%');
        HTMLUtils.css(document.getElementById('bottom'), 'width', '100%');
        HTMLUtils.css(document.getElementById('bottom'), 'height', (100 * (parent_dim.height - (widthAndHeightOfMRS.height + topAndLeftOfMRS.top)) / parent_dim.height) + '%');
    }

    getDim(): Rectangle {
        return this.dim;
    }

    setDim(rect: Rectangle) {
        this.dim = rect;
    }

    private getXCanvas(): HTMLCanvasElement {
        return this.container.getElement().querySelector('canvas#x');
    }
}
